/**
 * Settings → Security & compliance. Sign-in and audit controls for this workspace.
 * Every value on this page comes from a backend field with a real value — no
 * certification, auditor, number or date is written down here.
 * Route: settings.security (compliance.read).
 */
import { useEffect, useState, type ReactNode } from "react";
import { Btn, Card, Icon, PageLayout, PanelHeader, Pill, T } from "@v0y4ge/jrni-ui";
import { PageHeader } from "../../shell/chrome";
import SettingsShell from "./SettingsShell";
import { useT } from "../../lib/i18n";
import { useNav } from "../../shell/router";
import { getSsoSettings, type SsoSettings } from "./sso-api";

const IconAny = Icon as unknown as Record<string, (p?: { size?: number }) => ReactNode>;
const ic = (name: string, size = 14) => { const C = IconAny[name] || IconAny.info; return <C size={size} />; };

/* The SSO row reflects the server's real state and opens Settings > Security > Single
   sign-on (CC 0173). While the read is pending or refused it says so, never "On". */
function SsoControlRow() {
  const nav = useNav();
  const [s, setS] = useState<SsoSettings | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => { getSsoSettings().then(setS, () => setErr(true)); }, []);
  const msOn = !!s?.microsoft.enabled;
  /* SAML counts as on only when the connection is enabled AND this deployment has SAML
     switched on (CC 0199): with serviceProvider.available false the metadata answers 404
     and no SAML sign-in completes. */
  const samlAvailable = (s?.serviceProvider as { available?: boolean } | undefined)?.available === true;
  const samlSetUpUnavailable = !!s?.saml && !samlAvailable;
  const samlOn = !!s?.saml?.enabled && samlAvailable;
  const verified = s ? s.microsoft.domains.filter((d) => d.status === "verified").length : 0;
  const parts: string[] = [];
  if (s) {
    parts.push(msOn ? `Microsoft sign-in on for ${verified} ${verified === 1 ? "domain" : "domains"}` : "Microsoft sign-in off");
    parts.push(!s.saml ? "No SAML connection"
      : samlSetUpUnavailable ? "SAML connection set up, not available on this deployment"
      : samlOn ? "SAML connection on" : "SAML connection paused");
    const exp = s.saml?.certificate?.expiresAt ? Math.floor((+new Date(s.saml.certificate.expiresAt) - Date.now()) / 86_400_000) : null;
    if (exp !== null && exp < 0) parts.push("certificate expired");
    else if (exp !== null && exp <= 30) parts.push(`certificate expires in ${exp} ${exp === 1 ? "day" : "days"}`);
  }
  const sub = err ? "Status couldn't be loaded." : !s ? "Loading…" : parts.join(" · ") + ".";
  return (
    <div style={{ padding: T.space[4], border: `1px solid ${T.border}`, borderRadius: T.radius.md, display: "flex", gap: T.space[3], alignItems: "flex-start" }} data-testid="security-sso-row">
      <span style={{ width: 32, height: 32, borderRadius: T.radius.md, background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{ic("lock")}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
          <span style={{ fontSize: T.text.body.fontSize, fontWeight: 600, color: T.n1 }}>Single sign-on (SSO)</span>
          {s ? (msOn || samlOn ? <Pill tone="success" dot>On</Pill>
            : samlSetUpUnavailable && s.saml?.enabled ? <Pill tone="warn">Not available</Pill>
            : <Pill tone="neutral">Off</Pill>) : null}
        </div>
        <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginTop: T.space[1], lineHeight: 1.5 }}>{sub}</div>
        <div style={{ marginTop: T.space[2] }}>
          <Btn variant="ghost" size="sm" icon={ic("arrow", 11)} onClick={() => nav.go("settings.sso")}>Configure</Btn>
        </div>
      </div>
    </div>
  );
}

function AuditLogRow() {
  const t = useT();
  const nav = useNav();
  return (
    <div style={{ padding: T.space[4], border: `1px solid ${T.border}`, borderRadius: T.radius.md, display: "flex", gap: T.space[3], alignItems: "flex-start" }} data-testid="security-audit-row">
      <span style={{ width: 32, height: 32, borderRadius: T.radius.md, background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{ic("eye")}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: T.text.body.fontSize, fontWeight: 600, color: T.n1 }}>{t("settings.security.auditLogs", "Audit logs")}</span>
        <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginTop: T.space[1], lineHeight: 1.5 }}>{t("settings.security.auditLogsReviewChanges", "Review changes made in this workspace.")}</div>
        <div style={{ marginTop: T.space[2] }}>
          <Btn variant="ghost" size="sm" icon={ic("arrow", 11)} onClick={() => nav.go("settings.audit")}>{t("settings.security.openAuditLog", "Open audit log")}</Btn>
        </div>
      </div>
    </div>
  );
}

export default function SettingsSecurity() {
  const t = useT();
  return (
    <SettingsShell activeTab="security">
      <PageHeader
        title={t("settings.security.securityCompliance", "Security & compliance")}
        subtitle={t("settings.security.signInAndAuditControlsForThisWorkspace", "Sign-in and audit controls for this workspace")}
      />
      <PageLayout>
        <Card style={{ padding: 0 }}>
          <div style={{ padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}` }}>
            <PanelHeader divider={false} title={t("settings.security.securityStatus", "Security status")} />
          </div>
          <div style={{ padding: T.space[4], display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: T.space[3] }}>
            <SsoControlRow />
            <AuditLogRow />
          </div>
        </Card>
        <Card padding="compact" data-testid="security-compliance-note">
          <p style={{ margin: 0, fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.5 }}>{t("settings.security.complianceDocumentationOnRequest", "Compliance documentation is provided by jrni.ai on request.")}</p>
        </Card>
      </PageLayout>
    </SettingsShell>
  );
}
